import React from 'react'
import { Link } from 'react-router-dom'
import './sso-workspace.scss'
import './auth-card.scss'
import { ProviderLogo } from './provider-logo'
import { Pill } from './pill'
import { IconUser } from '../icon/icon'

// The one layout every sign-in journey page shares (/auth/email,
// /auth/providers, /auth/error, the password pages, /open/organization,
// /open/error): a centred card on the sunk ground, in the connection
// workspace's tokens so light and dark both follow from sso-workspace.scss.
// Pages compose these pieces rather than styling their own, so the journey
// reads as one product.

export const AuthPage = ({ children, wide, labelledBy }) =>
  <div className="sw-workspace sa-page">
    <main className={`sa-card ${wide ? 'sa-wide' : ''}`.trim()} aria-labelledby={labelledBy}>
      {children}
    </main>
  </div>

// The page's heading. `mark` (an AuthMark) sits beside the title; `lede` is
// one or two sentences under it.
export const AuthHead = ({ id, title, lede, mark }) =>
  <header className="sa-head">
    {mark
      ? <div className="sa-head-row">
          {mark}
          <h1 id={id}>{title}</h1>
        </div>
      : <h1 id={id}>{title}</h1>}
    {lede && <p>{lede}</p>}
  </header>

// A round tone mark (ok, warn, bad) beside an outcome's title.
export const AuthMark = ({ tone, children }) =>
  <span className={`sa-mark ${tone || ''}`.trim()} aria-hidden="true">{children}</span>

// The rule between two ways in: a line, the word, a line.
export const AuthDivider = ({ children = 'or' }) =>
  <div className="sa-divider" role="separator" aria-label={typeof children === 'string' ? children : undefined}>
    <span>{children}</span>
  </div>

// A tinted note. tone: info (default), warn, bad. Status by default, so a
// screen reader announces it when it appears.
export const AuthNotice = ({ tone, role = 'status', id, children }) =>
  <div className={`sa-notice ${tone || ''}`.trim()} role={role} id={id}>
    {children}
  </div>

// A working state: a small ring (still under reduced motion) and a sentence.
export const AuthLoading = ({ children }) =>
  <p className="sa-loading" role="status">
    <span className="sa-spin" aria-hidden="true" />
    <span>{children}</span>
  </p>

// The email being signed in, as a quiet read-only field, with "Change" as a
// small secondary button back to the email step.
export const AuthWho = ({ email, changeTo }) =>
  <div className="sa-who">
    <span className="sa-who-icon" aria-hidden="true"><IconUser /></span>
    <span className="sa-who-email" title={email || ''}>{email}</span>
    {changeTo &&
      <Link className="btn sw-btn sa-who-change" to={changeTo} aria-label="Use a different email">Change</Link>}
  </div>

// Handing off to a connection: its tile and the working line (which names it),
// centred in an inset panel.
export const AuthHandoff = ({ providerLink, children }) =>
  <div className="sa-handoff">
    <ProviderLogo iconSVG={providerLink && providerLink.iconSVG} />
    <AuthLoading>{children}</AuthLoading>
  </div>

// The connection a message is about: its logo tile and its name.
export const AuthConnection = ({ id, providerLink, prefix }) =>
  providerLink && providerLink.displayName
    ? <div className="sa-identity" id={id}>
        <ProviderLogo iconSVG={providerLink.iconSVG} />
        <span>
          {prefix && <>{prefix} </>}
          <strong>{providerLink.displayName}</strong>
        </span>
      </div>
    : null

// One choice in a list of connections or organizations. `logo` is whatever
// tile the list draws (ProviderLogo, an organization logo, an initial).
// `flag` is a short marker pill beside the name ("Last used").
// `flagTone` colours the pill (default info). `action` is a word in place of
// the chevron ("Restore") when picking the row does something particular.
// `current`: the row is where the person already is (aria-current, a quiet
// surface, no chevron) -- not somewhere to go.
export const AuthOption = ({ logo, name, flag, flagTone, sub, action, current, disabled, onClick }) =>
  <button type="button" className={`sa-option ${current ? 'sa-option-current' : ''}`.trim()} aria-current={current ? 'true' : undefined} disabled={disabled} onClick={onClick}>
    {logo}
    <span className="sa-option-name">
      <span className="sa-option-title">
        <span>{name}</span>
        {flag && <Pill tone={flagTone || 'info'}>{flag}</Pill>}
      </span>
      {sub && <span className="sa-option-sub">{sub}</span>}
    </span>
    {action
      ? <span className="sa-option-action">{action}</span>
      : !current && <span className="sa-chev" aria-hidden="true">›</span>}
  </button>
